/**
 * Author: Gaurav Kumar
 *
 * Module: api.js
 * This module provides utilities for authenticated Http Requests using MSAL and Tanstack React Query
 *
 * Features:
 * - MSAL Integration: Manages authentication tokens via MSAL, including token retrieval and silent refresh.
 * - React Query Integration: Provides hooks (`useAuthenticatedQuery`, `useAuthenticatedQueries`) to manage authenticated API calls.
 * - Session Token Management: Handles session storage for tokens (`AUTH_TOKEN_KEY`, `AUTOMATION_TOKEN`).
 *
 * Hooks:
 * - useIsAuthorized: Runs an effect to check if the user is authorized by retrieving a token from session storage.
 * - useAuthenticatedQuery: A wrapper around `useQuery` for making API calls with authentication, managing dependencies and error handling.
 * - useAuthenticatedQueries: A wrapper around `useQueries` to handle multiple API calls requiring authentication.
 *
 * Utilities:
 * - getAuthToken: Retrieves an authentication token from session storage or calls MSAL to acquire a new token.
 * - retrieveNewAuthToken: Uses MSAL to acquire a new token silently, storing it in session storage.
 * - httpCall: Makes an authenticated API call using Axios, handling msal token.
 */

import type { IMsalContext } from '@azure/msal-react';
import { useMsal } from '@azure/msal-react';
import type { NotifyOnChangeProps, UseQueryResult } from '@tanstack/react-query';
import { useQueries, useQuery } from '@tanstack/react-query';
import axios, { isAxiosError } from 'axios';
import type { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useCustomNavigate } from 'watchtower-ui/components/common/CustomNavigation';
import type { LoginResponse, User } from 'watchtower-ui/contexts/AuthContext';
import { FEATURE_FLAG_QUERY_PARAM, useFeatureFlag } from 'watchtower-ui/contexts/FeatureFlagContext';
import { ACCESS_TOKEN_KEY, AUTH_TOKEN_KEY, DEFAULT_API_URL } from 'watchtower-ui/utils/Constants';

// API code to our config code mapping (keep for future non-standard code mappings)
const API_CODE_MAP: Record<string, string> = {};

export interface RequestAccessPayload {
  countries: string[];
  reason: string;
}

/** @internal */
export const AUTOMATION_TOKEN = 'Automation_token';

export type Response<T> = { success: true; data: T } | { success: false; err?: AxiosResponse; message?: string };

export type NavigateFunction = ReturnType<typeof useCustomNavigate>;

export type AuthResponse = { token_type: string; access_token: string };

// Helper to check if error requires user interaction (e.g., expired refresh token)
const isInteractionRequiredError = (err: unknown): boolean => {
  const errorMessage = err instanceof Error ? err.message : String(err);
  const interactionRequiredPatterns = [
    'interaction_required',
    'invalid_grant',
    'AADSTS700084',
    'consent_required',
    'login_required',
  ];
  return interactionRequiredPatterns.some((pattern) => errorMessage.includes(pattern));
};

const retrieveNewAuthToken = async (
  msalDetails: IMsalContext,
  navigate: NavigateFunction | undefined,
  enableDeveloperLogs: boolean,
) => {
  const account = msalDetails.accounts[0];
  if (!account) {
    console.warn('Trying to retrieve a token with no active accounts.');
    return null;
  }
  try {
    const response = await msalDetails.instance.acquireTokenSilent({
      scopes: ['https://graph.microsoft.com/.default'],
      account,
      forceRefresh: true,
    });
    if (response.idToken) {
      // Use localStorage to match MSAL's cacheLocation and avoid conflicts with other apps on the same domain
      localStorage.setItem(AUTH_TOKEN_KEY, response.idToken);
      localStorage.setItem(ACCESS_TOKEN_KEY, response.accessToken);
      return [response.idToken, response.accessToken] as const;
    }
  } catch (err) {
    if (isAxiosError(err) && err.response?.status === 404) {
      navigate?.({ to: '/unauthorized', keptQueryParams: [FEATURE_FLAG_QUERY_PARAM] });
      return null;
    }

    if (isInteractionRequiredError(err)) {
      // Clear stale tokens and trigger fresh login
      localStorage.removeItem(AUTH_TOKEN_KEY);
      localStorage.removeItem(ACCESS_TOKEN_KEY);
      console.warn('Token expired or invalid. Triggering fresh login...');
      // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Fire and forget redirect
      msalDetails.instance.acquireTokenRedirect({
        scopes: ['https://graph.microsoft.com/.default'],
        account,
      });
      return null;
    }

    if (import.meta.env.DEV || enableDeveloperLogs) {
      console.warn(err);
    }
  }
  return null;
};

const getAuthToken = async (
  msalDetails: IMsalContext,
  navigate: NavigateFunction | undefined,
  enableDeveloperLogs: boolean,
) => {
  const token =
    sessionStorage.getItem(AUTOMATION_TOKEN) ??
    localStorage.getItem(AUTH_TOKEN_KEY) ??
    (await retrieveNewAuthToken(msalDetails, navigate, enableDeveloperLogs))?.[0];
  if (token) {
    return `Bearer ${token}`;
  } else {
    localStorage.removeItem(AUTH_TOKEN_KEY);
    return null;
  }
};

const getAccessToken = async (
  msalDetails: IMsalContext,
  navigate: NavigateFunction | undefined,
  enableDeveloperLogs: boolean,
) => {
  const token =
    localStorage.getItem(ACCESS_TOKEN_KEY) ??
    (await retrieveNewAuthToken(msalDetails, navigate, enableDeveloperLogs))?.[1];
  if (token) {
    return token;
  } else {
    return null;
  }
};

export const useIsAuthorized = () => {
  const msalDetails = useMsal();
  const navigate = useCustomNavigate();
  const [enableDeveloperLogs] = useFeatureFlag('DEVELOPER_LOGS');
  useEffect(() => {
    // eslint-disable-next-line no-void -- We just want to run this in the background
    void (async () => {
      await getAuthToken(msalDetails, navigate, enableDeveloperLogs);
    })();
  }, [msalDetails, navigate, enableDeveloperLogs]);
};

/**
 * Extracts a user-friendly error message from API response data.
 */
const extractApiErrorMessage = (data: unknown): string | undefined => {
  if (typeof data === 'string') return data;

  if (typeof data === 'object' && data !== null) {
    const obj = data as Record<string, unknown>;
    if (typeof obj.message === 'string') return obj.message;
    if (typeof obj.detail === 'string') return obj.detail;
    if (typeof obj.data === 'string') return obj.data;
    return JSON.stringify(data);
  }

  return undefined;
};

/**
 * Checks if the error is an authentication error (401/403).
 */
const isAuthError = (status: number | undefined): boolean => status === 401 || status === 403;

/**
 * Enhances an Axios error with the API error message for better error reporting.
 */
const enhanceErrorWithApiMessage = (err: AxiosError): void => {
  if (err.response && typeof err.response === 'object' && 'data' in err.response) {
    const apiMessage = extractApiErrorMessage(err.response.data);
    if (apiMessage) {
      err.message = apiMessage;
    }
  }
};

export const httpCall = async <T>(
  config: AxiosRequestConfig,
  msalDetails: IMsalContext,
  navigate: NavigateFunction | undefined = undefined,
  enableDeveloperLogs: boolean = false,
  recursiveCall: boolean = false,
): Promise<Response<T>> => {
  const token = await getAuthToken(msalDetails, navigate, enableDeveloperLogs);
  const accessToken = await getAccessToken(msalDetails, navigate, enableDeveloperLogs);
  if (!token) return { success: false };
  const headerConfig: AxiosRequestConfig = {
    ...config,
    headers: {
      ...config.headers,
      Authorization: token,
      'X-Access-Token': accessToken,
    },
    validateStatus: config.validateStatus ?? ((status) => status < 400),
  };
  try {
    const response = await axios<T>(headerConfig);
    return {
      success: true,
      data: response.data,
    };
  } catch (err) {
    console.error('got error from request', err, config);

    if (isAxiosError(err)) {
      // Handle authentication errors
      if (isAuthError(err.response?.status)) {
        localStorage.removeItem(AUTH_TOKEN_KEY);
        if (recursiveCall) {
          navigate?.({ to: '/', keptQueryParams: [FEATURE_FLAG_QUERY_PARAM] });
        } else {
          return httpCall(config, msalDetails, navigate, enableDeveloperLogs, true);
        }
      }

      // Enhance error with API message for better error reporting
      enhanceErrorWithApiMessage(err);
    }

    throw err;
  }
};

export type AuthQueryOptions = {
  dependencies?: boolean[];
  notifyOnChangeProps?: NotifyOnChangeProps;
};

export const useAuthenticatedQuery = <T>(
  queryKey: unknown[],
  queryConfig: AxiosRequestConfig,
  additionalOptions?: AuthQueryOptions,
  refetchOnWindowFocus?: boolean,
  staleTime?: number,
  initialData?: T | null,
): UseQueryResult<Response<T>> => {
  const msalDetails = useMsal();
  const navigate = useCustomNavigate();
  const [enableDeveloperLogs] = useFeatureFlag('DEVELOPER_LOGS');
  const initialDataObject = useMemo(() => {
    if (!initialData) {
      return undefined;
    }
    return {
      success: true,
      data: initialData,
    };
  }, [initialData]);
  const result = useQuery({
    queryKey,
    queryFn: () => httpCall<T>(queryConfig, msalDetails, navigate, enableDeveloperLogs),
    enabled: (additionalOptions?.dependencies ?? []).every(Boolean),
    notifyOnChangeProps: additionalOptions?.notifyOnChangeProps ?? ['data', 'error'],
    refetchOnWindowFocus: refetchOnWindowFocus ?? false,
    retry: false,
    throwOnError: false,
    retryOnMount: true,
    initialData: initialDataObject,
    staleTime,
  });
  const { isPending, error, data } = result;
  useEffect(() => {
    if (import.meta.env.DEV || enableDeveloperLogs) {
      if (error) {
        console.error(error);
      } else if (isPending) {
        console.debug(queryKey, 'Pending');
      } else {
        console.debug(queryKey, data);
      }
    }
  }, [enableDeveloperLogs, error, isPending, data, queryKey]);
  return result;
};

type UseAuthenticatedQueries = {
  queryKey: unknown[];
  queryConfig: AxiosRequestConfig;
  additionalOptions?: AuthQueryOptions;
  refetchOnWindowFocus?: boolean;
};

export const useAuthenticatedQueries = <T>(queryList: UseAuthenticatedQueries[]): UseQueryResult<Response<T>>[] => {
  const msalDetails = useMsal();
  const navigate = useCustomNavigate();
  const [enableDeveloperLogs] = useFeatureFlag('DEVELOPER_LOGS');
  const result = useQueries({
    queries: queryList.map(({ queryKey, queryConfig, additionalOptions, refetchOnWindowFocus }) => ({
      queryKey,
      queryFn: () => httpCall<T>(queryConfig, msalDetails, navigate, enableDeveloperLogs),
      enabled: (additionalOptions?.dependencies ?? []).every(Boolean),
      notifyOnChangeProps: additionalOptions?.notifyOnChangeProps ?? ['data', 'error'],
      refetchOnWindowFocus: refetchOnWindowFocus ?? false,
      retry: false,
      throwOnError: true,
      retryOnMount: true,
    })),
  });
  const error = useMemo(
    () =>
      result.reduce((axiosError, { error: axiosEr }) => {
        if (axiosEr) axiosError.push(axiosEr);
        return axiosError;
      }, [] as Error[]),
    [result],
  );
  const newIsPending = useMemo(() => result.map(({ isPending }) => isPending), [result]);
  const newData = useMemo(() => result.map(({ data }) => data), [result]);
  const newQueryKey = useMemo(() => queryList.map(({ queryKey }) => queryKey), [queryList]);
  useEffect(() => {
    if (import.meta.env.DEV || enableDeveloperLogs) {
      if (error.length > 0) {
        console.error(error);
      } else if (newIsPending.length > 0) {
        console.debug(newQueryKey, 'Pending');
      } else {
        console.debug(newQueryKey, newData);
      }
    }
  }, [enableDeveloperLogs, error, newIsPending, newData, newQueryKey]);
  return result;
};

// apis

/** @internal */
export const BASE_URL = import.meta.env.VITE_API_BASE_URL;
/** @internal */
export const LOGIN_URL = '/users/login';
/** @internal */
export const SCENARIO_URL = '/api/scenarios';
/** @internal */
export const HISTORICAL_URL = '/api/historical-decomp/';
/** @internal */
export const GET_USER_DETAILS_URL = 'api/users/info';
/** @internal */
export const GET_DRIVER_DISCRIPTION_URL = '/api/historical-decomp/mapping/driver-desc-url';
/** @internal */
export const GET_HISTORICAL_BRAND_URL = '/api/historical-decomp/homepage/landing-brands';
/** @internal */
export const GET_DRAFT_DATA = '/api/pnl-draft';
/** @internal */
export const POST_DRAFT_DATA = '/api/pnl-draft';
/** @internal */
export const GET_SCENARIO_DATA = '/api/optimizer/scenario';
/** @internal */
export const GET_PARTITION_DATA = '/api/commercial_priority_mapping';
/** @internal */
export const INVESTMENT_CHART = '/api/historical-decomp/insight/inv-ev-chart';
/** @internal */
export const DR_INVESTMENT_CHART = '/api/historical-decomp/insight/delta-inv-eff-chart';
/** @internal */
export const POST_SAVE_SIMULATION_DATA = '/api/save-simulation';
/** @internal */
export const DELETE_BULK_SIMULATION = '/api/delete-simulations';
/** @internal */
export const GET_DRIVERS_DATA = '/api/historical-decomp/mapping/chart-signals';
/** @internal */
export const GET_COLUMN_CHART_DATA = '/api/historical-decomp/insight/clustered-chart';
/** @internal */
export const GET_TOP_BOTTOM_CHART = '/api/historical-decomp/insight/top-bottom-chart';
/** @internal */
export const GET_STACKED_LINE_CHART = '/api/historical-decomp/insight/stacked-line-chart';
/** @internal */
export const GET_INVEST_EFFEC_CHART_DATA = '/api/historical-decomp/insight/invest-efficiency/tabular/revenue';
/** @internal */
export const POST_SAVE_PL_PROTOTYPE = '/api/optimizer/4pnl-opt:testing5/';
/** @internal */
export const GET_DISTRIBUTION_TABLE_DATA = '/api/historical-decomp/distribution/chart';
/** @internal */
export const POST_LONG_TERM_IMPACT = '/api/historical-decomp/long-term-impact';
/** @internal */
export const POST_MOONLIGHT_WALK_THROUGH = '/api/historical-decomp/moonlight-walk-through';
/** @internal */
export const POST_DRIVER_ANALYSIS = '/api/historical-decomp/driver-analysis';

/** @internal */
export const USER_LOGIN = async (
  msalDetails: IMsalContext,
  navigate: NavigateFunction,
  enableDeveloperLogs: boolean,
): Promise<LoginResponse> => {
  const authTokens = await retrieveNewAuthToken(msalDetails, navigate, enableDeveloperLogs);
  if (!authTokens) {
    throw new Error('Failed to retrieve auth tokens');
  }
  const [idToken, accessToken] = authTokens;
  try {
    const response = await axios.post<{ token: string; user: unknown }>(`${DEFAULT_API_URL}/v1/user/login`, {
      token: idToken,
      access_token: accessToken,
    });
    return {
      token: response.data.token,
      user: response.data.user as User,
    };
  } catch (error) {
    console.error('Error login:', error);
    // Re-throw original error to preserve status code for handling in AuthContext
    throw error;
  }
};

/**
 * Hook to fetch available markets from the API
 * @param excludedCountries - Array of country codes to exclude from the results
 * @returns Object with availableMarkets array and isLoadingMarkets boolean
 */
export const useAvailableMarkets = (excludedCountries: string[] = []) => {
  const [availableMarkets, setAvailableMarkets] = useState<string[]>([]);
  const [isLoadingMarkets, setIsLoadingMarkets] = useState(true);
  const hasFetchedRef = useRef(false);

  // Memoize the excluded countries string for stable comparison
  const excludedCountriesKey = useMemo(
    () => excludedCountries.toSorted((a, b) => a.localeCompare(b)).join(','),
    [excludedCountries],
  );

  useEffect(() => {
    // Prevent multiple fetches
    if (hasFetchedRef.current) return;
    hasFetchedRef.current = true;

    const fetchMarkets = async () => {
      try {
        const marketsUrl = `${DEFAULT_API_URL}/v1/markets`;
        const response = await fetch(marketsUrl);
        if (response.ok) {
          const data = (await response.json()) as Record<string, unknown>;
          const marketCodes = Object.keys(data).map((code) => API_CODE_MAP[code] || code);
          const excludedSet = new Set(excludedCountriesKey.split(',').filter(Boolean));
          setAvailableMarkets(marketCodes.filter((code) => !excludedSet.has(code)));
        }
      } catch (error) {
        console.error('Failed to fetch markets:', error);
        setAvailableMarkets([]);
      } finally {
        setIsLoadingMarkets(false);
      }
    };
    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- Async function in useEffect, errors handled internally
    fetchMarkets();
  }, [excludedCountriesKey]);

  return { availableMarkets, isLoadingMarkets };
};

/** @internal */
export const REQUEST_ACCESS = async (
  msalDetails: IMsalContext,
  navigate: NavigateFunction | undefined,
  enableDeveloperLogs: boolean,
  payload: RequestAccessPayload,
): Promise<Response<unknown>> => {
  // Optional developer logging
  if (import.meta.env.DEV || enableDeveloperLogs) {
    console.debug('REQUEST_ACCESS', 'Attempting to request access with payload:', payload);
  }

  const config: AxiosRequestConfig = {
    method: 'POST',
    url: `${DEFAULT_API_URL}/v1/user/request-access`,
    data: payload,
  };

  return httpCall(config, msalDetails, navigate, enableDeveloperLogs);
};
